import type { DiagnosticDto } from '@malou-io/package-dto';

import {
    BETTER_LOCAL_COMPETITORS_COUNT,
    type DiagnosticPartner,
    DiagnosticRating,
    FACEBOOK_SCORE_THRESHOLD,
    FACEBOOK_VISIBILITY_RATING,
    GOOGLE_SCORE_RATING_THRESHOLD,
    INCONSISTENCIES_RATING_THRESHOLD,
    INSTAGRAM_SCORE_THRESHOLD,
    INSTAGRAM_VISIBILITY_RATING,
    isNotNil,
    KEYWORDS_RATING_THRESHOLD,
    type MaloupeLocale,
    MINIMUM_GOOGLE_RATING_FOR_GOOD_SCORE,
    PHOTOS_RATING_THRESHOLD,
    REVIEW_COUNT_RATING,
    roundToDecimals,
    SCORE_RATING,
    SERVICES_RATING_THRESHOLD,
    TimeInMilliseconds,
    TOTAL_SCORE_WITHOUT_INSTAGRAM,
} from '@malou-io/package-utils';

import { ScoreCriteria } from ':shared/enums';

import { Address } from './address';
import { Category } from './category';
import { DiagnosticKeyword } from './keyword';
import { DiagnosticReview } from './review';

export class Diagnostic {
    id: string;
    placeId: string;
    isMalouRestaurant: boolean;
    restaurant: {
        name: string;
        address: Address;
        category: Category;
        averageRatingForSimilarRestaurants?: number;
        betterCompetitorCount?: number;
        rating: number | null;
        services: {
            acceptsCashOnly: boolean | null;
            acceptsCreditCards: boolean | null;
            acceptsDebitCards: boolean | null;
            acceptsNfc: boolean | null;
            allowsDogs: boolean | null;
            curbsidePickup: boolean | null;
            delivery: boolean | null;
            dineIn: boolean | null;
            freeParkingLot: boolean | null;
            freeStreetParking: boolean | null;
            goodForChildren: boolean | null;
            goodForGroups: boolean | null;
            goodForWatchingSports: boolean | null;
            liveMusic: boolean | null;
            menuForChildren: boolean | null;
            outdoorSeating: boolean | null;
            paidStreetParking: boolean | null;
            reservable: boolean | null;
            restroom: boolean | null;
            servesBeer: boolean | null;
            servesBreakfast: boolean | null;
            servesBrunch: boolean | null;
            servesCocktails: boolean | null;
            servesCoffee: boolean | null;
            servesDessert: boolean | null;
            servesDinner: boolean | null;
            servesLunch: boolean | null;
            servesVegetarianFood: boolean | null;
            servesWine: boolean | null;
            takeout: boolean | null;
            wheelchairAccessibleParking: boolean | null;
            wheelchairAccessibleSeating: boolean | null;
        };
    };
    language: MaloupeLocale;
    photoCount: number;
    reviewCount: number;
    averageReviewCountForSimilarRestaurants?: number;
    semanticAnalysisOverviewByLang?: {
        [MaloupeLocale.FR]: string;
        [MaloupeLocale.EN]: string;
    };
    createdAt: Date;
    similarRestaurantCount: number;
    keywords: DiagnosticKeyword[];
    reviews?: DiagnosticReview[];
    inconsistencies?: {
        platformCount: number;
        inconsistencyCount: number;
        platformKeys: string[];
    };
    instagramPage?: {
        userName: string;
        name: string;
        profilePictureUrl?: string;
        postCount: number;
        averageLikeCount: number;
        followerCount: number;
        averagePostCountForSimilarRestaurants: number;
        averageFollowerCountForSimilarRestaurants: number;
        averageLikeCountForSimilarRestaurants: number;
    } | null;
    facebookPage?: {
        name: string;
        postCount: number;
        followerCount: number;
        averageLikeCount: number;
        averagePostCountForSimilarRestaurants: number;
        averageFollowerCountForSimilarRestaurants: number;
        averageLikeCountForSimilarRestaurants: number;
    };
    partner?: DiagnosticPartner | null;

    constructor(diagnosticDto: DiagnosticDto | Diagnostic) {
        this.id = diagnosticDto.id;
        this.placeId = diagnosticDto.placeId;
        this.restaurant = {
            name: diagnosticDto.restaurant.name,
            address: new Address(diagnosticDto.restaurant.address),
            category: new Category(diagnosticDto.restaurant.category),
            averageRatingForSimilarRestaurants: diagnosticDto.restaurant.averageRatingForSimilarRestaurants
                ? roundToDecimals(diagnosticDto.restaurant.averageRatingForSimilarRestaurants, 1)
                : 0,
            betterCompetitorCount: diagnosticDto.restaurant.betterCompetitorCount,
            rating: diagnosticDto.restaurant.rating ?? null,
            services: diagnosticDto.restaurant.services,
        };
        this.language = diagnosticDto.language as MaloupeLocale;
        this.photoCount = diagnosticDto.photoCount;
        this.reviewCount = diagnosticDto.reviewCount ?? 0;
        this.averageReviewCountForSimilarRestaurants = roundToDecimals(diagnosticDto.averageReviewCountForSimilarRestaurants ?? 0, 0);
        this.semanticAnalysisOverviewByLang = diagnosticDto.semanticAnalysisOverviewByLang;
        this.createdAt = diagnosticDto.createdAt ? new Date(diagnosticDto.createdAt) : new Date();
        this.similarRestaurantCount = diagnosticDto.similarRestaurantCount;
        this.keywords = diagnosticDto.keywords?.map((keyword) => new DiagnosticKeyword(keyword)) ?? [];
        this.reviews = diagnosticDto.reviews?.map((review) => new DiagnosticReview(review)) ?? [];
        this.inconsistencies = diagnosticDto.inconsistencies;
        this.instagramPage = diagnosticDto.instagramPage ?? null;
        this.facebookPage = diagnosticDto.facebookPage ?? undefined;
        this.isMalouRestaurant = diagnosticDto.isMalouRestaurant;
        this.partner = diagnosticDto.partner ?? null;
    }

    getTotalScore(): number {
        const subTotalWithoutInstagram =
            this.getInconsistenciesScore() +
            this.getKeywordsScore() +
            this.getGoogleScore() +
            this.getPhotosScore() +
            this.getServicesScore() +
            this.getReviewCountScore();

        if (this.instagramPage?.userName) {
            return subTotalWithoutInstagram + this.getInstagramScore();
        }
        return roundToDecimals((subTotalWithoutInstagram / TOTAL_SCORE_WITHOUT_INSTAGRAM) * 100, 0);
    }

    getTotalScoreRating(): DiagnosticRating {
        const score = this.getTotalScore();
        if (score < SCORE_RATING.MINIMUM_AVERAGE_THRESHOLD) {
            return DiagnosticRating.BAD;
        }
        if (score < SCORE_RATING.MINIMUM_GOOD_THRESHOLD) {
            return DiagnosticRating.AVERAGE;
        }
        return DiagnosticRating.GOOD;
    }

    getInconsistencyRate(): number {
        const inconsistencies = this.inconsistencies;
        if (!inconsistencies) {
            return INCONSISTENCIES_RATING_THRESHOLD.MAXIMUM_AVERAGE_THRESHOLD;
        }
        return (inconsistencies.inconsistencyCount / inconsistencies.platformCount) * 100;
    }

    getInconsistencyRating(): DiagnosticRating {
        const inconsistencies = this.inconsistencies;
        if (!inconsistencies) {
            return DiagnosticRating.BAD;
        }
        const inconsistencyRate = this.getInconsistencyRate();
        if (inconsistencyRate < INCONSISTENCIES_RATING_THRESHOLD.MAXIMUM_GOOD_THRESHOLD) {
            return DiagnosticRating.GOOD;
        }
        if (inconsistencyRate < INCONSISTENCIES_RATING_THRESHOLD.MAXIMUM_AVERAGE_THRESHOLD) {
            return DiagnosticRating.AVERAGE;
        }
        return DiagnosticRating.BAD;
    }

    getInconsistenciesScore(): number {
        const rating = this.getInconsistencyRating();
        switch (rating) {
            case DiagnosticRating.GOOD:
                return INCONSISTENCIES_RATING_THRESHOLD.GOOD_SCORE;
            case DiagnosticRating.AVERAGE:
                return INCONSISTENCIES_RATING_THRESHOLD.AVERAGE_SCORE;
            default:
                return INCONSISTENCIES_RATING_THRESHOLD.BAD_SCORE;
        }
    }

    getKeywords(numberOfKeywords = 2): DiagnosticKeyword[] {
        if (this.isMalouRestaurant) {
            return this.keywords.sort((a, b) => a.googlePosition - b.googlePosition).slice(0, numberOfKeywords);
        }
        const sortedKeywords = this.keywords.sort((a, b) => b.googlePosition - a.googlePosition);
        if (sortedKeywords.length < 2) {
            return sortedKeywords;
        }
        const firstKeyword = sortedKeywords[0];
        const secondKeyword =
            sortedKeywords.slice(1).find((keyword) => keyword.googlePosition !== firstKeyword.googlePosition) ?? sortedKeywords[1];
        return [secondKeyword, firstKeyword];
    }

    getKeywordsAverageRating(): number {
        const chosenKeywords = this.getKeywords();
        if (!chosenKeywords.length) {
            return KEYWORDS_RATING_THRESHOLD.MAXIMUM_AVERAGE_THRESHOLD;
        }

        const rating = chosenKeywords.reduce((acc, keyword) => acc + keyword.googlePosition, 0);
        return rating / chosenKeywords.length;
    }

    getKeywordsRating(): DiagnosticRating {
        const chosenKeywords = this.getKeywords();
        if (!chosenKeywords.length) {
            return DiagnosticRating.BAD;
        }

        const rating = chosenKeywords.reduce((acc, keyword) => acc + keyword.googlePosition, 0);
        const averageRating = rating / chosenKeywords.length;

        const maximumGoodThreshHold = this.isMalouRestaurant
            ? KEYWORDS_RATING_THRESHOLD.MAXIMUM_GOOD_THRESHOLD_FOR_MALOU_CLIENTS
            : KEYWORDS_RATING_THRESHOLD.MAXIMUM_GOOD_THRESHOLD;
        const maximumAverageThreshHold = this.isMalouRestaurant
            ? KEYWORDS_RATING_THRESHOLD.MAXIMUM_AVERAGE_THRESHOLD_FOR_MALOU_CLIENTS
            : KEYWORDS_RATING_THRESHOLD.MAXIMUM_AVERAGE_THRESHOLD;

        if (averageRating <= maximumGoodThreshHold) {
            return DiagnosticRating.GOOD;
        }
        if (averageRating < maximumAverageThreshHold) {
            return DiagnosticRating.AVERAGE;
        }
        return DiagnosticRating.BAD;
    }

    getKeywordsScore(): number {
        const rating = this.getKeywordsRating();
        switch (rating) {
            case DiagnosticRating.GOOD:
                return KEYWORDS_RATING_THRESHOLD.GOOD_SCORE;
            case DiagnosticRating.AVERAGE:
                return KEYWORDS_RATING_THRESHOLD.AVERAGE_SCORE;
            default:
                return KEYWORDS_RATING_THRESHOLD.BAD_SCORE;
        }
    }

    getGoogleRating(): DiagnosticRating {
        const averageRatingForSimilarRestaurants = this.restaurant.averageRatingForSimilarRestaurants;
        const rating = this.restaurant.rating;
        if (!rating) {
            return DiagnosticRating.BAD;
        }
        if (rating >= MINIMUM_GOOGLE_RATING_FOR_GOOD_SCORE) {
            return DiagnosticRating.GOOD;
        }
        if (!averageRatingForSimilarRestaurants || !rating) {
            return DiagnosticRating.BAD;
        }
        const minimumRatingToBeAverage = averageRatingForSimilarRestaurants * GOOGLE_SCORE_RATING_THRESHOLD.MINIMUM_AVERAGE_THRESHOLD;
        if (rating < minimumRatingToBeAverage) {
            return DiagnosticRating.BAD;
        }
        const minimumRatingToBeGood = averageRatingForSimilarRestaurants * GOOGLE_SCORE_RATING_THRESHOLD.MINIMUM_GOOD_THRESHOLD;
        if (rating < minimumRatingToBeGood) {
            return DiagnosticRating.AVERAGE;
        }
        return DiagnosticRating.GOOD;
    }

    getGoogleScore(): number {
        const rating = this.getGoogleRating();
        switch (rating) {
            case DiagnosticRating.GOOD:
                return GOOGLE_SCORE_RATING_THRESHOLD.GOOD_SCORE;
            case DiagnosticRating.AVERAGE:
                return GOOGLE_SCORE_RATING_THRESHOLD.AVERAGE_SCORE;
            default:
                return GOOGLE_SCORE_RATING_THRESHOLD.BAD_SCORE;
        }
    }

    getBetterLocalCompetitorCount(): number {
        const betterCompetitorCount = this.restaurant.betterCompetitorCount ?? 0;
        switch (this.getGoogleRating()) {
            case DiagnosticRating.GOOD:
                return Math.min(betterCompetitorCount, BETTER_LOCAL_COMPETITORS_COUNT.MAXIMUM_COMPETITOR_COUNT_FOR_GOOD_RATING);
            case DiagnosticRating.AVERAGE:
                return Math.min(betterCompetitorCount, BETTER_LOCAL_COMPETITORS_COUNT.MAXIMUM_COMPETITOR_COUNT_FOR_AVERAGE_RATING);
            case DiagnosticRating.BAD:
                return Math.max(betterCompetitorCount, BETTER_LOCAL_COMPETITORS_COUNT.MINIMUM_COMPETITOR_COUNT_FOR_BAD_RATING);
            default:
                return 0;
        }
    }

    getInstagramRating(): {
        followersDiagnosticRating: DiagnosticRating;
        likesDiagnosticRating: DiagnosticRating;
        postCountDiagnosticRating: DiagnosticRating;
    } {
        const instagramPageData = this.instagramPage;
        if (instagramPageData) {
            const followersDiagnosticRating = this._getInstagramRatingForKpi({
                restaurantKpiValue: instagramPageData.followerCount,
                similarRestaurantsKpiValue: instagramPageData.averageFollowerCountForSimilarRestaurants,
                isClient: this.isMalouRestaurant,
                isPostCountKpi: false,
            });
            const likesDiagnosticRating = this._getInstagramRatingForKpi({
                restaurantKpiValue: instagramPageData.averageLikeCount,
                similarRestaurantsKpiValue: instagramPageData.averageLikeCountForSimilarRestaurants,
                isClient: this.isMalouRestaurant,
                isPostCountKpi: false,
            });
            const postCountDiagnosticRating = this._getInstagramRatingForKpi({
                restaurantKpiValue: instagramPageData.postCount,
                similarRestaurantsKpiValue: instagramPageData.averagePostCountForSimilarRestaurants,
                isClient: this.isMalouRestaurant,
                isPostCountKpi: true,
            });
            return { followersDiagnosticRating, likesDiagnosticRating, postCountDiagnosticRating };
        }
        return {
            followersDiagnosticRating: DiagnosticRating.BAD,
            likesDiagnosticRating: DiagnosticRating.BAD,
            postCountDiagnosticRating: DiagnosticRating.BAD,
        };
    }

    getInstagramScore(): number {
        const rating = this.getInstagramRating();
        return Object.values(rating).reduce((acc, value) => {
            switch (value) {
                case DiagnosticRating.GOOD:
                    return acc + INSTAGRAM_SCORE_THRESHOLD.GOOD_SCORE;
                case DiagnosticRating.AVERAGE:
                    return acc + INSTAGRAM_SCORE_THRESHOLD.AVERAGE_SCORE;
                default:
                    return acc + INSTAGRAM_SCORE_THRESHOLD.BAD_SCORE;
            }
        }, 1);
    }

    getInstagramVisibilityRating(): DiagnosticRating {
        const instagramScore = this.getInstagramScore();
        if (instagramScore < INSTAGRAM_VISIBILITY_RATING.MINIMUM_AVERAGE_THRESHOLD) {
            return DiagnosticRating.BAD;
        } else if (instagramScore < INSTAGRAM_VISIBILITY_RATING.MINIMUM_GOOD_THRESHOLD) {
            return DiagnosticRating.AVERAGE;
        }
        return DiagnosticRating.GOOD;
    }

    getFacebookRating(): {
        followersDiagnosticRating: DiagnosticRating;
        likesDiagnosticRating: DiagnosticRating;
        postCountDiagnosticRating: DiagnosticRating;
    } {
        const facebookPageData = this.facebookPage;
        if (facebookPageData) {
            const followersDiagnosticRating = this._getFacebookRatingForKpi({
                restaurantKpiValue: facebookPageData.followerCount,
                similarRestaurantsKpiValue: facebookPageData.averageFollowerCountForSimilarRestaurants,
                isClient: this.isMalouRestaurant,
                isPostCountKpi: false,
            });
            const likesDiagnosticRating = this._getFacebookRatingForKpi({
                restaurantKpiValue: facebookPageData.averageLikeCount,
                similarRestaurantsKpiValue: facebookPageData.averageLikeCountForSimilarRestaurants,
                isClient: this.isMalouRestaurant,
                isPostCountKpi: false,
            });
            const postCountDiagnosticRating = this._getFacebookRatingForKpi({
                restaurantKpiValue: facebookPageData.postCount,
                similarRestaurantsKpiValue: facebookPageData.averagePostCountForSimilarRestaurants,
                isClient: this.isMalouRestaurant,
                isPostCountKpi: true,
            });
            return { followersDiagnosticRating, likesDiagnosticRating, postCountDiagnosticRating };
        }
        return {
            followersDiagnosticRating: DiagnosticRating.BAD,
            likesDiagnosticRating: DiagnosticRating.BAD,
            postCountDiagnosticRating: DiagnosticRating.BAD,
        };
    }

    getFacebookScore(): number {
        const rating = this.getFacebookRating();
        return Object.values(rating).reduce((acc, value) => {
            switch (value) {
                case DiagnosticRating.GOOD:
                    return acc + FACEBOOK_SCORE_THRESHOLD.GOOD_SCORE;
                case DiagnosticRating.AVERAGE:
                    return acc + FACEBOOK_SCORE_THRESHOLD.AVERAGE_SCORE;
                default:
                    return acc + FACEBOOK_SCORE_THRESHOLD.BAD_SCORE;
            }
        }, 1);
    }

    getFacebookVisibilityRating(): DiagnosticRating {
        const facebookScore = this.getFacebookScore();
        if (facebookScore < FACEBOOK_VISIBILITY_RATING.MINIMUM_AVERAGE_THRESHOLD) {
            return DiagnosticRating.BAD;
        } else if (facebookScore < FACEBOOK_VISIBILITY_RATING.MINIMUM_GOOD_THRESHOLD) {
            return DiagnosticRating.AVERAGE;
        }
        return DiagnosticRating.GOOD;
    }

    getPhotosRating(): DiagnosticRating {
        const photoCount = this.photoCount;
        if (photoCount < PHOTOS_RATING_THRESHOLD.MINIMUM_AVERAGE_THRESHOLD) {
            return DiagnosticRating.BAD;
        }
        if (photoCount < PHOTOS_RATING_THRESHOLD.MINIMUM_GOOD_THRESHOLD) {
            return DiagnosticRating.AVERAGE;
        }
        return DiagnosticRating.GOOD;
    }

    getPhotosScore(): number {
        const rating = this.getPhotosRating();
        switch (rating) {
            case DiagnosticRating.GOOD:
                return PHOTOS_RATING_THRESHOLD.GOOD_SCORE;
            case DiagnosticRating.AVERAGE:
                return PHOTOS_RATING_THRESHOLD.AVERAGE_SCORE;
            default:
                return PHOTOS_RATING_THRESHOLD.BAD_SCORE;
        }
    }

    getServiceCount(): number {
        return Object.values(this.restaurant.services).length;
    }

    getPresentServiceCount(): number {
        return Object.values(this.restaurant.services).filter(isNotNil).length;
    }

    getServicesRating(): DiagnosticRating {
        const serviceCount = this.getServiceCount();
        if (serviceCount === 0) {
            return DiagnosticRating.BAD;
        }
        const presentServiceCount = this.getPresentServiceCount();
        const ratio = (presentServiceCount / serviceCount) * 100;

        if (ratio < SERVICES_RATING_THRESHOLD.MINIMUM_AVERAGE_THRESHOLD) {
            return DiagnosticRating.BAD;
        }
        if (ratio < SERVICES_RATING_THRESHOLD.MINIMUM_GOOD_THRESHOLD) {
            return DiagnosticRating.AVERAGE;
        }
        return DiagnosticRating.GOOD;
    }

    getServicesScore(): number {
        const rating = this.getServicesRating();
        switch (rating) {
            case DiagnosticRating.GOOD:
                return SERVICES_RATING_THRESHOLD.GOOD_SCORE;
            case DiagnosticRating.AVERAGE:
                return SERVICES_RATING_THRESHOLD.AVERAGE_SCORE;
            default:
                return SERVICES_RATING_THRESHOLD.BAD_SCORE;
        }
    }

    getReviewCountDiff(): number {
        const reviewCount = this.reviewCount;
        const averageReviewCountForSimilarRestaurants = this.averageReviewCountForSimilarRestaurants ?? 0;

        return averageReviewCountForSimilarRestaurants - reviewCount;
    }

    getReviewCountRating(): DiagnosticRating {
        const reviewCount = this.reviewCount;
        const averageReviewCountForSimilarRestaurants = this.averageReviewCountForSimilarRestaurants ?? 0;

        if (reviewCount === 0 || averageReviewCountForSimilarRestaurants === 0) {
            return DiagnosticRating.BAD;
        }
        const ratio = (reviewCount / averageReviewCountForSimilarRestaurants) * 100;
        const isMalouRestaurant = this.isMalouRestaurant;
        const minimumAverageThreshold = isMalouRestaurant
            ? REVIEW_COUNT_RATING.THRESHOLDS.MALOU.MINIMUM_AVERAGE_THRESHOLD
            : REVIEW_COUNT_RATING.THRESHOLDS.LEADS.MINIMUM_AVERAGE_THRESHOLD;
        if (ratio < minimumAverageThreshold) {
            return DiagnosticRating.BAD;
        }
        const minimumGoodThreshold = isMalouRestaurant
            ? REVIEW_COUNT_RATING.THRESHOLDS.MALOU.MINIMUM_GOOD_THRESHOLD
            : REVIEW_COUNT_RATING.THRESHOLDS.LEADS.MINIMUM_GOOD_THRESHOLD;
        if (ratio < minimumGoodThreshold) {
            return DiagnosticRating.AVERAGE;
        }
        return DiagnosticRating.GOOD;
    }

    getReviewCountScore(): number {
        const rating = this.getReviewCountRating();
        switch (rating) {
            case DiagnosticRating.GOOD:
                return REVIEW_COUNT_RATING.SCORES.GOOD;
            case DiagnosticRating.AVERAGE:
                return REVIEW_COUNT_RATING.SCORES.AVERAGE;
            default:
                return REVIEW_COUNT_RATING.SCORES.BAD;
        }
    }

    isNewDiagnostic(): boolean {
        const createdAt = this.createdAt;
        const currentDate = new Date();
        const diffInMs = currentDate.getTime() - createdAt.getTime();
        return diffInMs < 5 * TimeInMilliseconds.MINUTE;
    }

    getScoreCriterions(): {
        criteria: ScoreCriteria;
        diagnosticRating?: DiagnosticRating;
        instagramRating?: {
            followersDiagnosticRating: DiagnosticRating;
            likesDiagnosticRating: DiagnosticRating;
            postCountDiagnosticRating: DiagnosticRating;
        };
    }[] {
        const inconsistencyRating = this.getInconsistencyRating();
        const googleRating = this.getGoogleRating();
        const reviewCountRating = this.getReviewCountRating();
        const instagramRating = this.getInstagramRating();
        return [
            {
                criteria: ScoreCriteria.INCONSISTENCIES,
                diagnosticRating: inconsistencyRating,
            },
            {
                criteria: ScoreCriteria.BETTER_LOCAL_COMPETITORS,
                diagnosticRating: googleRating,
            },
            { criteria: ScoreCriteria.REVIEW_COUNT, diagnosticRating: reviewCountRating },
            { criteria: ScoreCriteria.INSTAGRAM_VISIBILITY, instagramRating },
        ];
    }

    private _getInstagramRatingForKpi({
        restaurantKpiValue,
        similarRestaurantsKpiValue,
        isClient,
        isPostCountKpi,
    }: {
        restaurantKpiValue: number;
        similarRestaurantsKpiValue: number;
        isClient: boolean;
        isPostCountKpi: boolean;
    }): DiagnosticRating {
        let minimumAverageThreshold = INSTAGRAM_SCORE_THRESHOLD.MINIMUM_AVERAGE_THRESHOLD;
        let minimumGoodThreshold = INSTAGRAM_SCORE_THRESHOLD.MINIMUM_GOOD_THRESHOLD;

        if (isClient && !isPostCountKpi) {
            minimumAverageThreshold = INSTAGRAM_SCORE_THRESHOLD.MINIMUM_AVERAGE_THRESHOLD_FOR_MALOU_CLIENTS;
            minimumGoodThreshold = INSTAGRAM_SCORE_THRESHOLD.MINIMUM_GOOD_THRESHOLD_FOR_MALOU_CLIENTS;
        }
        if (restaurantKpiValue < similarRestaurantsKpiValue * minimumAverageThreshold) {
            return DiagnosticRating.BAD;
        } else if (restaurantKpiValue <= similarRestaurantsKpiValue * minimumGoodThreshold) {
            return DiagnosticRating.AVERAGE;
        }
        return DiagnosticRating.GOOD;
    }

    private _getFacebookRatingForKpi({
        restaurantKpiValue,
        similarRestaurantsKpiValue,
        isClient,
        isPostCountKpi,
    }: {
        restaurantKpiValue: number;
        similarRestaurantsKpiValue: number;
        isClient: boolean;
        isPostCountKpi: boolean;
    }): DiagnosticRating {
        let minimumAverageThreshold = FACEBOOK_SCORE_THRESHOLD.MINIMUM_AVERAGE_THRESHOLD;
        let minimumGoodThreshold = FACEBOOK_SCORE_THRESHOLD.MINIMUM_GOOD_THRESHOLD;

        if (isClient && !isPostCountKpi) {
            minimumAverageThreshold = FACEBOOK_SCORE_THRESHOLD.MINIMUM_AVERAGE_THRESHOLD_FOR_MALOU_CLIENTS;
            minimumGoodThreshold = FACEBOOK_SCORE_THRESHOLD.MINIMUM_GOOD_THRESHOLD_FOR_MALOU_CLIENTS;
        }
        if (restaurantKpiValue < similarRestaurantsKpiValue * minimumAverageThreshold) {
            return DiagnosticRating.BAD;
        } else if (restaurantKpiValue <= similarRestaurantsKpiValue * minimumGoodThreshold) {
            return DiagnosticRating.AVERAGE;
        }
        return DiagnosticRating.GOOD;
    }
}
